import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "./cn";

/**
 * Details — the explanation a card carries, behind one tap.
 *
 * The consoles' rule (owner, 2026-11-02): ONE visible sentence per card. What GOVERNS a value — the
 * limit it is measured against, whose setting applies, that a figure is shared by the whole account —
 * stays visible next to the value (CLAUDE.md rule 1); the essay about why goes here. Nothing is
 * deleted: the text a person needs once is one tap away instead of on screen every visit.
 *
 * A native <details>: keyboard, screen readers and find-in-page all work with nothing added.
 */
export function Details({
  summary = "Details",
  children,
  className,
  defaultOpen,
}: {
  summary?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  defaultOpen?: boolean;
}) {
  return (
    <details className={cn("group text-[12.5px] leading-relaxed text-muted", className)} open={defaultOpen}>
      <summary className="inline-flex min-h-8 cursor-pointer list-none items-center gap-1 text-[12.5px] font-medium text-accent select-none hover:underline pointer-coarse:min-h-10 [&::-webkit-details-marker]:hidden">
        <ChevronRight size={13} aria-hidden className="shrink-0 transition-transform group-open:rotate-90" />
        {summary}
      </summary>
      <div className="mt-1.5 space-y-2">{children}</div>
    </details>
  );
}

/**
 * Lead — the ONE sentence a card shows, optionally followed by its Details. The common shape of
 * "say what this is, keep the rest a tap away", so screens do not each hand-build it.
 */
export function Lead({
  children,
  details,
  detailsLabel,
  className,
}: {
  children: React.ReactNode;
  details?: React.ReactNode;
  detailsLabel?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("text-[13px] leading-relaxed text-muted", className)}>
      <p>{children}</p>
      {details && (
        <Details summary={detailsLabel ?? "More about this"} className="mt-1">
          {details}
        </Details>
      )}
    </div>
  );
}
